import { useState, useEffect } from "react";

interface UseFilterPersistenceOptions<T> {
  storageKey: string;
  initialState: T | (() => T);
  onRestore?: (savedData: T) => Promise<void>;
}

export function useFilterPersistence<T>({
  storageKey,
  initialState,
  onRestore,
}: UseFilterPersistenceOptions<T>) {
  const [filterState, setFilterState] = useState<T>(() => {
    const savedFilters = sessionStorage.getItem(storageKey);
    const initialValue =
      initialState instanceof Function ? initialState() : initialState;

    if (savedFilters) {
      const parsedFilters = JSON.parse(savedFilters);
      return {
        ...initialValue,
        ...parsedFilters,
      };
    }
    return initialValue;
  });

  const [filterChanged, setFilterChanged] = useState(false);
  const [clearFilter, setClearFilter] = useState(true);

  // Track filter changes
  useEffect(() => {
    if (clearFilter) {
      setClearFilter(false);
      setFilterChanged(false);
    } else {
      setFilterChanged(true);
    }
    sessionStorage.removeItem(storageKey);
  }, [clearFilter, filterState, storageKey]);

  const setFilterStateWithTracking = (newState: T | ((prev: T) => T)) => {
    setFilterState(newState);
  };

  const saveFilters = (filters: T) => {
    sessionStorage.setItem(storageKey, JSON.stringify(filters));
  };

  const clearFilters = () => {
    sessionStorage.removeItem(storageKey);
    setClearFilter(true);
  };

  // Load saved data and trigger onRestore callback
  useEffect(() => {
    const loadSavedData = async () => {
      const savedFilters = sessionStorage.getItem(storageKey);
      if (savedFilters && onRestore) {
        const parsedFilters = JSON.parse(savedFilters);
        await onRestore(parsedFilters);
      }
    };

    loadSavedData();
  }, [storageKey, onRestore]);

  return {
    filterState,
    setFilterState: setFilterStateWithTracking,
    saveFilters,
    clearFilters,
    filterChanged,
    clearFilter,
    setClearFilter,
  };
}
